(() => {
  if (window.__bnAccountDashboardRuntimeV1) return;
  window.__bnAccountDashboardRuntimeV1 = true;
  'use strict';

  const MOBILE_QUERY = '(max-width: 749px)';
  const AUTOPLAY_MS = 4200;
  const AVATAR_STORAGE_PREFIX = 'bnDashboardAvatar:';
  const carouselRuntimes = new WeakMap();
  const avatarRuntimes = new WeakMap();

  const initCarousel = (carousel) => {
    if (!carousel || carousel.dataset.bnCarouselMounted === 'true') return;

    const track = carousel.querySelector('[data-bn-carousel-track]');
    const slides = Array.from(carousel.querySelectorAll('[data-bn-carousel-slide]'));
    const prevButton = carousel.querySelector('[data-bn-carousel-prev]');
    const nextButton = carousel.querySelector('[data-bn-carousel-next]');
    const dotsRoot = carousel.querySelector('[data-bn-carousel-dots]');

    if (!track || slides.length <= 1 || !dotsRoot) {
      if (prevButton) prevButton.hidden = true;
      if (nextButton) nextButton.hidden = true;
      if (dotsRoot) dotsRoot.hidden = true;
      return;
    }

    carousel.dataset.bnCarouselMounted = 'true';
    const controller = new AbortController();
    const { signal } = controller;

    const mediaQuery = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
    let activeIndex = 0;
    let autoplayTimer = null;
    let scrollTimer = null;
    let isPaused = false;

    dotsRoot.innerHTML = '';
    dotsRoot.dataset.bnCarouselTotal = String(slides.length);
    dotsRoot.dataset.bnCarouselMode = slides.length > 7 ? 'compact' : 'numeric';

    const dots = slides.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bn-student-dashboard__carousel-dot';
      dot.setAttribute('aria-label', `Ir al curso ${index + 1} de ${slides.length}`);
      dot.addEventListener('click', () => goTo(index, true), { signal });
      dotsRoot.appendChild(dot);
      return dot;
    });

    const isMobile = () => !mediaQuery || mediaQuery.matches;

    const setActive = (index) => {
      activeIndex = Math.max(0, Math.min(slides.length - 1, index));
      dotsRoot.dataset.bnCarouselActive = String(activeIndex + 1);

      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeIndex;
        dot.textContent = dotsRoot.dataset.bnCarouselMode === 'compact' && isActive ? `${dotIndex + 1} / ${slides.length}` : `${dotIndex + 1}`;
        dot.setAttribute('aria-label', isActive ? `Curso ${dotIndex + 1} de ${slides.length}` : `Ir al curso ${dotIndex + 1} de ${slides.length}`);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const goTo = (index, userInitiated = false) => {
      const nextIndex = (index + slides.length) % slides.length;
      setActive(nextIndex);

      if (isMobile()) {
        const slide = slides[nextIndex];
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: userInitiated ? 'smooth' : 'smooth' });
      }

      if (userInitiated) restartAutoplay();
    };

    const syncFromScroll = () => {
      if (!isMobile()) return;
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        const trackLeft = track.scrollLeft;
        let closestIndex = 0;
        let closestDistance = Infinity;

        slides.forEach((slide, index) => {
          const distance = Math.abs((slide.offsetLeft - track.offsetLeft) - trackLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        setActive(closestIndex);
      }, 90);
    };

    const stopAutoplay = () => {
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!isMobile() || isPaused || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      autoplayTimer = window.setInterval(() => goTo(activeIndex + 1), AUTOPLAY_MS);
    };

    const restartAutoplay = () => {
      stopAutoplay();
      startAutoplay();
    };

    if (prevButton) prevButton.addEventListener('click', () => goTo(activeIndex - 1, true), { signal });
    if (nextButton) nextButton.addEventListener('click', () => goTo(activeIndex + 1, true), { signal });

    track.addEventListener('scroll', syncFromScroll, { passive: true, signal });
    carousel.addEventListener('mouseenter', () => { isPaused = true; stopAutoplay(); }, { signal });
    carousel.addEventListener('mouseleave', () => { isPaused = false; startAutoplay(); }, { signal });
    carousel.addEventListener('focusin', () => { isPaused = true; stopAutoplay(); }, { signal });
    carousel.addEventListener('focusout', () => { isPaused = false; startAutoplay(); }, { signal });
    carousel.addEventListener('touchstart', () => { isPaused = true; stopAutoplay(); }, { passive: true, signal });
    carousel.addEventListener('touchend', () => { isPaused = false; startAutoplay(); }, { passive: true, signal });

    let legacyMediaChangeHandler = null;
    if (mediaQuery) {
      const onChange = () => {
        setActive(activeIndex);
        if (isMobile()) startAutoplay();
        else stopAutoplay();
      };

      if (typeof mediaQuery.addEventListener === 'function') mediaQuery.addEventListener('change', onChange, { signal });
      else if (typeof mediaQuery.addListener === 'function') {
        legacyMediaChangeHandler = onChange;
        mediaQuery.addListener(onChange);
      }
    }

    carouselRuntimes.set(carousel, {
      destroy() {
        controller.abort();
        stopAutoplay();
        window.clearTimeout(scrollTimer);
        if (legacyMediaChangeHandler && typeof mediaQuery?.removeListener === 'function') {
          mediaQuery.removeListener(legacyMediaChangeHandler);
        }
        carouselRuntimes.delete(carousel);
        delete carousel.dataset.bnCarouselMounted;
      },
    });

    setActive(0);
    startAutoplay();
  };

  const safeReadAvatar = (key) => {
    try {
      const value = window.localStorage.getItem(key);
      if (!value) return null;
      const parsed = JSON.parse(value);
      if (!parsed || typeof parsed.value !== 'string') return null;
      return parsed;
    } catch (error) {
      return null;
    }
  };

  const safeWriteAvatar = (key, avatar) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(avatar));
    } catch (error) {
      return false;
    }
    return true;
  };

  const safeRemoveAvatar = (key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      return false;
    }
    return true;
  };

  const initAvatarPicker = (root) => {
    if (!root || root.dataset.bnAvatarMounted === 'true') return;

    const display = root.querySelector('[data-bn-avatar-display]');
    const modal = root.querySelector('[data-bn-avatar-modal]');
    const openButtons = Array.from(root.querySelectorAll('[data-bn-avatar-open]'));
    const closeButtons = Array.from(root.querySelectorAll('[data-bn-avatar-close]'));
    const backdrop = root.querySelector('[data-bn-avatar-backdrop]');
    const clearButton = root.querySelector('[data-bn-avatar-clear]');
    const choices = Array.from(root.querySelectorAll('[data-bn-avatar-choice]'));

    if (!display || !modal || !openButtons.length || !choices.length) return;

    root.dataset.bnAvatarMounted = 'true';
    const controller = new AbortController();
    const { signal } = controller;
    let focusTimer = null;

    const storageIdentity = root.getAttribute('data-bn-avatar-key') || root.getAttribute('data-bn-avatar-initial') || window.location.pathname;
    const storageKey = `${AVATAR_STORAGE_PREFIX}${storageIdentity}`;
    const defaultHtml = display.innerHTML;
    let lastFocusedElement = null;

    const updateSelectedChoice = (avatar) => {
      choices.forEach((choice) => {
        const isSelected = !!avatar && choice.dataset.bnAvatarType === avatar.type && choice.dataset.bnAvatarValue === avatar.value;
        choice.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
    };

    const applyAvatar = (avatar) => {
      if (!avatar || !avatar.value) {
        display.innerHTML = defaultHtml;
        display.removeAttribute('data-bn-avatar-custom');
        display.removeAttribute('data-bn-avatar-type');
        updateSelectedChoice(null);
        return;
      }

      const type = avatar.type || 'text';
      const value = avatar.value;
      const avatarValue = document.createElement('span');
      avatarValue.className = 'bn-dashboard-avatar__value';
      avatarValue.textContent = value;

      display.innerHTML = '';
      display.appendChild(avatarValue);
      display.setAttribute('data-bn-avatar-custom', 'true');
      display.setAttribute('data-bn-avatar-type', type);
      updateSelectedChoice({ type, value });
    };

    const openModal = () => {
      lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      modal.hidden = false;
      document.documentElement.classList.add('bn-avatar-picker-is-open');

      const selectedChoice = choices.find((choice) => choice.getAttribute('aria-pressed') === 'true');
      window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        (selectedChoice || choices[0])?.focus({ preventScroll: true });
      }, 30);
    };

    const closeModal = () => {
      modal.hidden = true;
      document.documentElement.classList.remove('bn-avatar-picker-is-open');
      if (lastFocusedElement) lastFocusedElement.focus({ preventScroll: true });
    };

    openButtons.forEach((button) => {
      button.addEventListener('click', openModal, { signal });
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeModal, { signal });
    });

    if (backdrop) backdrop.addEventListener('click', closeModal, { signal });

    choices.forEach((choice) => {
      choice.addEventListener('click', () => {
        const avatar = {
          type: choice.dataset.bnAvatarType || 'text',
          value: choice.dataset.bnAvatarValue || choice.textContent.trim()
        };
        applyAvatar(avatar);
        safeWriteAvatar(storageKey, avatar);
      }, { signal });
    });

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        safeRemoveAvatar(storageKey);
        applyAvatar(null);
      }, { signal });
    }

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
      }
    }, { signal });

    avatarRuntimes.set(root, {
      destroy() {
        controller.abort();
        window.clearTimeout(focusTimer);
        if (!modal.hidden) {
          modal.hidden = true;
          document.documentElement.classList.remove('bn-avatar-picker-is-open');
        }
        avatarRuntimes.delete(root);
        delete root.dataset.bnAvatarMounted;
      },
    });

    applyAvatar(safeReadAvatar(storageKey));
  };

  const revealLockedMessage = (trigger) => {
    const scope = trigger.closest('[data-bn-locked-scope]') || trigger.parentElement || document;
    const message = scope.querySelector('[data-bn-locked-message]');
    if (!message) return;

    message.hidden = false;
    message.setAttribute('data-bn-visible', 'true');
  };

  const initLockedTriggers = () => {
    document.querySelectorAll('[data-bn-locked-trigger]').forEach((trigger) => {
      if (trigger.dataset.bnLockedMounted === 'true') return;
      trigger.dataset.bnLockedMounted = 'true';

      trigger.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) return;
        if (trigger.tagName !== 'A') event.preventDefault();
        revealLockedMessage(trigger);
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        revealLockedMessage(trigger);
      });
    });
  };

  const collectWithin = (scope, selector) => {
    const nodes = [];
    if (scope?.matches?.(selector)) nodes.push(scope);
    scope?.querySelectorAll?.(selector).forEach((node) => nodes.push(node));
    return nodes;
  };

  const destroyWithin = (scope) => {
    collectWithin(scope, '[data-bn-dashboard-carousel]').forEach((node) => carouselRuntimes.get(node)?.destroy());
    collectWithin(scope, '[data-bn-avatar-root]').forEach((node) => avatarRuntimes.get(node)?.destroy());
  };

  const init = () => {
    document.querySelectorAll('[data-bn-dashboard-carousel]').forEach(initCarousel);
    document.querySelectorAll('[data-bn-avatar-root]').forEach(initAvatarPicker);
    initLockedTriggers();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
